// ---------------------------------------------------------------------------
// Duplicate-client guard. Archiving never deletes a client's data — but a
// second record with the same name (e.g. Searson Collision Center, Oct 2026)
// makes it easy to reactivate the EMPTY copy and think the data is gone.
// Content data lives on the client doc's own files/gsc/chat_logs subcollections,
// so it only shows up under the record it was uploaded to.
// ---------------------------------------------------------------------------
import { collection, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';

const normName = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const normUrl  = s => String(s || '').trim().toLowerCase()
  .replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '').replace(/[^a-z0-9./-]/g, '');

// Every OTHER client doc sharing the business name or website.
export async function findDuplicateClients({ id, businessName, clientUrl }) {
  const name = normName(businessName);
  const url  = normUrl(clientUrl);
  const snap = await getDocs(collection(db, 'clients'));
  return snap.docs
    .filter(d => d.id !== id)
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(c => (name && normName(c.businessName) === name) || (url && normUrl(c.clientUrl) === url));
}

const fileCount = async id => {
  try { return (await getCountFromServer(collection(db, 'clients', id, 'files'))).data().count; }
  catch { return '?'; }
};

// Confirm before reactivating when another record shares this client's name/URL.
// Shows each record's Content Data file count so the real one is obvious.
export async function confirmReactivate(client) {
  const dupes = await findDuplicateClients(client);
  if (!dupes.length) return true;
  const mine = await fileCount(client.id);
  const lines = await Promise.all(dupes.map(async d =>
    `• ${d.businessName} (${d.isArchived ? 'archived' : 'ACTIVE'}) — ${await fileCount(d.id)} content files`));
  return window.confirm(
    `There is more than one "${client.businessName}" record.\n\n` +
    `This one has ${mine} content files.\nOther record(s):\n${lines.join('\n')}\n\n` +
    `Content data only shows on the record it was uploaded to. Reactivate THIS one?`
  );
}

// Two-step guard before a permanent delete (Torrente Properties was deleted by
// accident, Oct 2026). First steers toward Archive, then requires typing the
// business name so a stray click can't get through.
export function confirmDeleteClient(client) {
  const name = client.businessName || 'this client';
  if (!window.confirm(
    `Are you sure you want to PERMANENTLY delete "${name}"?\n\n` +
    `This cannot be undone. To pause a client, cancel and use Archive instead.`
  )) return false;
  const typed = window.prompt(`To confirm, type the business name exactly:\n\n${name}`);
  if (typed === null) return false;
  if (normName(typed) !== normName(client.businessName)) {
    window.alert('Name did not match. Nothing was deleted.');
    return false;
  }
  return true;
}
